// ============================================================================
// [RCV_PICKER_V1] 수신자 선택 (공용) — 2026-11-02
// [RCV_PICKER_V2] ③ 수신자를 그룹 제목(📁) 아래에 묶어서 표시 + 그룹 체크 = 그룹 전체 선택
//
//  순서: ① 카테고리(직원/거래처/고객…) → ② 그룹 → ③ 수신자
//   ① 카테고리를 고르면 그 카테고리에 있는 그룹만 ②에 보인다
//   ② 그룹을 고르면 그 그룹의 사람만 ③에 보인다 (같은 이름의 그룹이 카테고리마다 따로 있을 수 있어
//      "카테고리+그룹" 한 쌍으로 구분 — 예: 거래처/상가_부동산 ≠ 고객/상가_부동산)
//   ③ 고른 그룹마다 📁 제목 아래에 소속 수신자 — 제목 체크 = 그룹 전체, 개별 체크, [보이는 사람 모두 선택]
//   · 선택은 화면 필터와 별개로 유지된다 (카테고리를 바꿔도 이미 고른 사람은 그대로)
//
//  쓰는 곳
//   SmsDirectSend.jsx  설정탭 > SMS 전송
//   EstateEditor.jsx   매물 상세 > SMS 발송 창
//
//  props
//   receivers      [{ id, name, phone, category, group_name }]  (/api/sms-receivers 응답 그대로)
//   selectedIds    선택된 id 배열
//   onChange(ids)  선택이 바뀔 때 새 배열을 넘겨줌
//   maxHeight      ③ 목록 높이 (기본 220px)
// ============================================================================
import React, { useMemo, useState } from 'react';

const NONE_GROUP = '미지정';                         // 그룹이 비어 있는 사람
const keyOf = (r) => (r.category || '기타') + '\u0001' + (r.group_name || NONE_GROUP);

const fmtPhone = (v = '') => {
  const n = String(v).replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  if (n.length === 10) return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}`;
  return v;
};

// 단계 표시 줄 (① ② ③ 제목)
const StepTitle = ({ no, children, right }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 5px' }}>
    <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
      <span style={{
        display: 'inline-block', width: 18, height: 18, lineHeight: '18px', textAlign: 'center',
        borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: '11px', marginRight: 5,
      }}>{no}</span>
      {children}
    </span>
    {right}
  </div>
);

// 선택 칩 (카테고리·그룹 공용)
const Chip = ({ on, onClick, label, count }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '4px 11px', borderRadius: '14px', fontSize: '12px', cursor: 'pointer',
      border: `1px solid ${on ? '#2563eb' : '#cbd5e0'}`,
      backgroundColor: on ? '#2563eb' : '#fff',
      color: on ? '#fff' : '#475569',
      fontWeight: on ? 'bold' : 'normal',
    }}
  >
    {label}
    {count != null && <span style={{ marginLeft: 4, opacity: 0.8 }}>{count}</span>}
  </button>
);

const smallBtn = {
  fontSize: '11px', border: '1px solid #cbd5e0', background: '#fff',
  borderRadius: '3px', cursor: 'pointer', padding: '2px 8px',
};

export default function ReceiverPicker({ receivers = [], selectedIds = [], onChange, maxHeight = '220px' }) {
  const [cats, setCats] = useState([]);        // ① 고른 카테고리
  const [groups, setGroups] = useState([]);    // ② 고른 "카테고리+그룹" 키

  const list = Array.isArray(receivers) ? receivers : [];
  const sel = new Set(selectedIds);
  const setSel = (next) => onChange && onChange([...next]);

  // ① 카테고리 목록 (인원 수 포함)
  const catList = useMemo(() => {
    const m = new Map();
    list.forEach((r) => { const c = r.category || '기타'; m.set(c, (m.get(c) || 0) + 1); });
    return [...m.entries()];
  }, [list]);

  // ② 고른 카테고리 안의 그룹 목록
  const groupList = useMemo(() => {
    const m = new Map();
    list.filter((r) => cats.includes(r.category || '기타')).forEach((r) => {
      const k = keyOf(r);
      const g = m.get(k) || { key: k, cat: r.category || '기타', name: r.group_name || NONE_GROUP, n: 0 };
      g.n += 1; m.set(k, g);
    });
    return [...m.values()].sort((a, b) => (a.cat + a.name).localeCompare(b.cat + b.name, 'ko'));
  }, [list, cats]);

  // ③ 고른 그룹의 사람
  const people = useMemo(
    () => list.filter((r) => groups.includes(keyOf(r)))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko')),
    [list, groups]
  );

  // [RCV_PICKER_V2] ③ 표시용 — 고른 그룹 순서(②와 같은 순서)대로 소속 수신자를 묶음
  const peopleByGroup = useMemo(
    () => groupList
      .filter((g) => groups.includes(g.key))
      .map((g) => ({ ...g, rows: people.filter((r) => keyOf(r) === g.key) }))
      .filter((g) => g.rows.length > 0),
    [groupList, groups, people]
  );
  const toggleGroupMembers = (rows) => {
    const all = rows.every((r) => sel.has(r.id));
    const next = new Set(sel);
    rows.forEach((r) => (all ? next.delete(r.id) : next.add(r.id)));
    setSel(next);
  };

  const toggleCat = (c) => {
    const next = cats.includes(c) ? cats.filter((x) => x !== c) : [...cats, c];
    setCats(next);
    // 카테고리를 빼면 그 카테고리의 그룹도 ②에서 같이 뺀다 (선택된 수신자는 유지)
    setGroups((gs) => gs.filter((k) => next.includes(k.split('\u0001')[0])));
  };
  const toggleGroup = (k) => setGroups((gs) => (gs.includes(k) ? gs.filter((x) => x !== k) : [...gs, k]));
  const allGroups = () => setGroups(groupList.map((g) => g.key));

  const toggleOne = (id) => {
    const next = new Set(sel);
    next.has(id) ? next.delete(id) : next.add(id);
    setSel(next);
  };
  const allShown = people.length > 0 && people.every((r) => sel.has(r.id));
  const toggleShown = () => {
    const next = new Set(sel);
    people.forEach((r) => (allShown ? next.delete(r.id) : next.add(r.id)));
    setSel(next);
  };

  // 지금 화면 밖(다른 카테고리·그룹)에 선택돼 있는 사람 수 — 헷갈리지 않게 알려준다
  const hiddenSelected = selectedIds.filter((id) => !people.some((r) => r.id === id)).length;

  if (list.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: '#a0aec0', fontSize: '12px', background: '#fff', borderRadius: 4 }}>
        등록된 수신처가 없습니다. [수신처 관리]에서 등록해주세요.
      </div>
    );
  }

  return (
    <div>
      {/* 선택 요약 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 700 }}>
          선택 {selectedIds.length}명
          {hiddenSelected > 0 && (
            <span style={{ fontWeight: 'normal', color: '#94a3b8' }}> (다른 그룹 {hiddenSelected}명 포함)</span>
          )}
        </span>
        {selectedIds.length > 0 && (
          <button type="button" style={smallBtn} onClick={() => setSel([])}>전체 해제</button>
        )}
      </div>

      {/* ① 카테고리 */}
      <StepTitle no="1">카테고리</StepTitle>
      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
        {catList.map(([c, n]) => (
          <Chip key={c} on={cats.includes(c)} onClick={() => toggleCat(c)} label={c} count={n} />
        ))}
      </div>

      {/* ② 그룹 */}
      <StepTitle
        no="2"
        right={groupList.length > 1 && (
          <button type="button" style={smallBtn} onClick={allGroups}>그룹 모두</button>
        )}
      >
        그룹
      </StepTitle>
      {cats.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#94a3b8' }}>먼저 카테고리를 고르세요</div>
      ) : (
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {groupList.map((g) => (
            <Chip
              key={g.key}
              on={groups.includes(g.key)}
              onClick={() => toggleGroup(g.key)}
              label={cats.length > 1 ? `${g.cat}·${g.name}` : g.name}
              count={g.n}
            />
          ))}
        </div>
      )}

      {/* ③ 수신자 */}
      <StepTitle
        no="3"
        right={people.length > 0 && (
          <button type="button" style={smallBtn} onClick={toggleShown}>
            {allShown ? '보이는 사람 해제' : `보이는 ${people.length}명 모두 선택`}
          </button>
        )}
      >
        수신자
      </StepTitle>
      <div style={{ maxHeight, overflowY: 'auto', background: '#fff', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
        {people.length === 0 ? (
          <div style={{ padding: '14px', textAlign: 'center', color: '#a0aec0', fontSize: '12px' }}>
            {cats.length === 0 ? '카테고리 → 그룹을 차례로 고르세요' : '그룹을 고르세요'}
          </div>
        ) : peopleByGroup.map((g) => {
          const allIn = g.rows.every((r) => sel.has(r.id));
          return (
            <div key={g.key} style={{ borderBottom: '1px solid #edf2f7' }}>
              {/* 그룹 제목 — 체크하면 이 그룹 전체 선택/해제 */}
              <label style={{ background: '#edf2f7', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={allIn} onChange={() => toggleGroupMembers(g.rows)} />
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#2b6cb0' }}>
                  📁 {cats.length > 1 ? `${g.cat}·${g.name}` : g.name}
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>({g.rows.length})</span>
              </label>
              {/* 그룹 소속 수신자 */}
              {g.rows.map((r) => (
                <label
                  key={r.id}
                  style={{ padding: '5px 10px 5px 26px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}
                >
                  <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleOne(r.id)} />
                  <span style={{ flex: 1 }}>{r.name}</span>
                  <span style={{ color: '#64748b' }}>{fmtPhone(r.phone)}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
